import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Car,
  FileText,
  Shield,
  Upload,
  CheckCircle,
  Clock,
  ArrowLeft,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

const BecomeDriverPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    license_plate: '',
    seats_available: 3,
    license_doc_url: '',
    insurance_doc_url: '',
    vehicle_photo_url: '',
    bio: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    checkExistingProfile();
  }, [user, navigate]);

  const checkExistingProfile = async () => {
    try {
      const response = await axios.get(`${API}/carpool/my-driver-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingProfile(response.data);
    } catch (error) {
      // No existing profile
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_make || !formData.vehicle_model || !formData.license_plate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/carpool/driver-profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted! An admin will review your profile.');
      navigate('/carpool');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Show status if already applied
  if (existingProfile) {
    const statusConfig = {
      pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50', message: 'Your application is being reviewed by an admin.' },
      approved: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', message: 'You are an approved driver! You can now create carpool routes.' },
      rejected: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', message: 'Your application was not approved. Contact admin for details.' },
      suspended: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', message: 'Your driver account has been suspended.' }
    };
    const status = statusConfig[existingProfile.status] || statusConfig.pending;

    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/carpool')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Carpool
          </Button>

          <Card className={`${status.bg} border-0`}>
            <CardContent className="p-8 text-center">
              <status.icon className={`w-16 h-16 mx-auto mb-4 ${status.color}`} />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Application {existingProfile.status.charAt(0).toUpperCase() + existingProfile.status.slice(1)}
              </h2>
              <p className="text-slate-600 mb-6">{status.message}</p>
              
              {existingProfile.status === 'approved' && (
                <Button onClick={() => navigate('/carpool/my-routes')} className="bg-emerald-600 hover:bg-emerald-700">
                  <Car className="w-4 h-4 mr-2" /> Create a Route
                </Button>
              )}
              
              <div className="mt-8 p-4 bg-white rounded-lg text-left">
                <h3 className="font-medium mb-3">Your Vehicle Info</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Vehicle</p>
                    <p className="font-medium">{existingProfile.vehicle_make} {existingProfile.vehicle_model} ({existingProfile.vehicle_year})</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Color</p>
                    <p className="font-medium">{existingProfile.vehicle_color}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">License Plate</p>
                    <p className="font-medium">{existingProfile.license_plate}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Available Seats</p>
                    <p className="font-medium">{existingProfile.seats_available}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/carpool')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Carpool
        </Button>

        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Car className="w-6 h-6 text-emerald-500" />
              Become a Driver
            </CardTitle>
            <p className="text-slate-600 text-sm">
              Share rides with your Killarney neighbors. All applications are reviewed by admin.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Vehicle Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Car className="w-4 h-4" /> Vehicle Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Make *</Label>
                    <Input
                      value={formData.vehicle_make}
                      onChange={(e) => setFormData(p => ({ ...p, vehicle_make: e.target.value }))}
                      placeholder="e.g., Toyota"
                      required
                      data-testid="vehicle-make"
                    />
                  </div>
                  <div>
                    <Label>Model *</Label>
                    <Input
                      value={formData.vehicle_model}
                      onChange={(e) => setFormData(p => ({ ...p, vehicle_model: e.target.value }))}
                      placeholder="e.g., Camry"
                      required
                      data-testid="vehicle-model"
                    />
                  </div>
                  <div>
                    <Label>Year *</Label>
                    <Input
                      value={formData.vehicle_year}
                      onChange={(e) => setFormData(p => ({ ...p, vehicle_year: e.target.value }))}
                      placeholder="e.g., 2020"
                      required
                    />
                  </div>
                  <div>
                    <Label>Color *</Label>
                    <Input
                      value={formData.vehicle_color}
                      onChange={(e) => setFormData(p => ({ ...p, vehicle_color: e.target.value }))}
                      placeholder="e.g., Silver"
                      required
                    />
                  </div>
                  <div>
                    <Label>License Plate *</Label>
                    <Input
                      value={formData.license_plate}
                      onChange={(e) => setFormData(p => ({ ...p, license_plate: e.target.value }))}
                      placeholder="e.g., ABC-1234"
                      required
                      data-testid="license-plate"
                    />
                  </div>
                  <div>
                    <Label>Available Seats *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={formData.seats_available}
                      onChange={(e) => setFormData(p => ({ ...p, seats_available: parseInt(e.target.value) }))}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Documents (Upload URLs)
                </h3>
                <p className="text-sm text-slate-500">
                  Upload your documents to a file service (Google Drive, Dropbox) and paste the share links below.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label>Driver's License (URL)</Label>
                    <Input
                      value={formData.license_doc_url}
                      onChange={(e) => setFormData(p => ({ ...p, license_doc_url: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                    />
                  </div>
                  <div>
                    <Label>Vehicle Insurance (URL)</Label>
                    <Input
                      value={formData.insurance_doc_url}
                      onChange={(e) => setFormData(p => ({ ...p, insurance_doc_url: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                    />
                  </div>
                  <div>
                    <Label>Vehicle Photo (URL)</Label>
                    <Input
                      value={formData.vehicle_photo_url}
                      onChange={(e) => setFormData(p => ({ ...p, vehicle_photo_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label>About You</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(p => ({ ...p, bio: e.target.value }))}
                  placeholder="Tell riders a bit about yourself - how long you've lived in Killarney, your driving experience, etc."
                  rows={3}
                />
              </div>

              {/* Terms */}
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-emerald-800">Safety First</p>
                    <p className="text-emerald-700">
                      By applying, you agree to maintain a valid driver's license and insurance,
                      drive safely, and treat all riders with respect. Admin will review your application.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-6"
                data-testid="submit-driver-application"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Submit Application
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BecomeDriverPage;
